import React, { useEffect, useState } from "react";
import "./Regulations.css";

import TemplateFilter from "./RegulationsFilter";
import TemplateTable from "./RegulationsTable";
import TemplatePagination from "./RegulationsPagination";
import { getDataDetail, getDataList } from "../../../../../api/dataApi";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const normalizeItem = (item) => ({
  id: item.id || item.docId || item.councilDocId || item.councilDocumentId,
  title: item.title || "",
  writer: item.writer || item.userName || item.createdBy || "총학생회",
  date: formatDate(item.date || item.createdAt),
  rawDate: item.date || item.createdAt || item.updatedAt || "",
  view: item.viewer ?? item.view ?? 0,
  content: item.content || item.contents || item.description || "",
  category: item.category || item.docCategory || "",
});

const getPagedArray = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.content)) return result.data.content;

  return [];
};

const sortLatestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateB - dateA;
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

const getDetailContent = async (id) => {
  if (!id) return "";

  try {
    const detail = await getDataDetail("stdcouncil", id);

    return detail?.content || detail?.contents || detail?.description || "";
  } catch (error) {
    console.error("총학생회 회칙/세칙 상세 내용 조회 실패:", error);
    return "";
  }
};

export default function Regulations() {
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("title");

  const [dataFilterType, setDataFilterType] = useState("all");

  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  const loadMissingContents = async () => {
    const hasMissingContent = data.some((item) => item.id && !item.content);

    if (!hasMissingContent) return;

    try {
      setContentLoading(true);

      const nextData = await Promise.all(
        data.map(async (item) => {
          if (!item.id || item.content) return item;

          const detailContent = await getDetailContent(item.id);

          return {
            ...item,
            content: detailContent,
          };
        })
      );

      setData(nextData);
    } finally {
      setContentLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchType === "content") {
      await loadMissingContents();
    }

    setSearchQuery(search);
    setPage(1);
  };

  useEffect(() => {
    const fetchRegulations = async () => {
      try {
        setLoading(true);

        const result = await getDataList({
          category: "stdcouncil",
          activeDataType: "rule",
          dataFilterType,
          searchCategory: "TITLE",
          keyWord: "",
          page: 0,
          size: 100,
        });

        const list = getPagedArray(result)
          .map(normalizeItem)
          .sort(sortLatestFirst);

        setData(list);
      } catch (error) {
        console.error("총학생회 회칙/세칙 목록 조회 실패:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRegulations();
  }, [dataFilterType]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchType === "content") {
        await loadMissingContents();
      }

      setSearchQuery(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchType]);

  useEffect(() => {
    setPage(1);
  }, [dataFilterType]);

  const filteredData = data.filter((item) => {
    const keyword = searchQuery.trim();

    if (!keyword) return true;

    if (searchType === "title") {
      return (item.title || "").includes(keyword);
    }

    return (item.content || "").includes(keyword);
  });

  const itemsPerPage = 10;
  const total = filteredData.length;
  const totalPages = Math.ceil(total / itemsPerPage);
  const safePage = total === 0 ? 1 : Math.min(page, totalPages || 1);

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(1);
    }
  }, [total, page, totalPages]);

  const pagedData = filteredData.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  return (
    <div className="regul-template">
      <div className="regul-template-inner">
        <div className="regul-template-header">
          민주적인 학생자치활동의 근간이 되는 총학생회 회칙과 각종 시행세칙을 확인하실 수 있습니다.
        </div>

        <TemplateFilter
          search={search}
          setSearch={setSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          dataFilterType={dataFilterType}
          setDataFilterType={setDataFilterType}
          handleSearch={handleSearch}
        />

        <div className="regul-template-layout">
          <div className="regul-template-content">
            {loading || contentLoading ? (
              <div className="regul-template-empty">불러오는 중입니다.</div>
            ) : (
              <TemplateTable
                data={pagedData}
                page={safePage}
                itemsPerPage={itemsPerPage}
                total={total}
              />
            )}
          </div>

          {total > 0 && (
            <TemplatePagination
              page={safePage}
              setPage={setPage}
              total={total}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}